import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  FolderOpen, 
  MessageSquare, 
  FileText, 
  TrendingUp,
  Mail,
  Clock,
  Loader2,
  Database
} from 'lucide-react';
import { getStatistics, seedData } from '@/lib/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const fetchStats = async () => {
    try {
      const response = await getStatistics();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast.error('İstatistikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await seedData();
      toast.success('Örnek veriler başarıyla eklendi!');
      fetchStats();
    } catch (error) {
      console.error('Error seeding data:', error);
      toast.error('Veri ekleme hatası');
    } finally {
      setSeeding(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: tr });
    } catch {
      return dateString;
    }
  };

  const statCards = [
    {
      title: 'Toplam Kategori',
      value: stats?.total_categories || 0,
      icon: FolderOpen,
      color: 'bg-blue-500'
    },
    {
      title: 'Toplam Hizmet',
      value: stats?.total_services || 0,
      icon: FileText,
      color: 'bg-green-500'
    },
    {
      title: 'Toplam Mesaj',
      value: stats?.total_messages || 0,
      icon: MessageSquare,
      color: 'bg-purple-500'
    },
    {
      title: 'Okunmamış Mesaj',
      value: stats?.unread_messages || 0,
      icon: Mail,
      color: 'bg-orange-500'
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Welcome Section */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h2>
            <p className="text-slate-600">Eta Global Group yönetim paneline hoş geldiniz.</p>
          </div>
          <Button 
            onClick={handleSeedData}
            disabled={seeding}
            data-testid="seed-data-btn"
            className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
          >
            {seeding ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Yükleniyor...
              </>
            ) : (
              <>
                <Database className="w-4 h-4" />
                Örnek Veri Ekle
              </>
            )}
          </Button>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-20 bg-slate-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => (
              <Card 
                key={index} 
                data-testid={`stat-card-${index}`}
                className="stats-card border-slate-200"
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold text-slate-800">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Recent Messages */}
        <Card className="border-slate-200" data-testid="recent-messages-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#005CB2]" />
              Son Mesajlar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-16 bg-slate-100 rounded" />
                  </div>
                ))}
              </div>
            ) : stats?.recent_messages && stats.recent_messages.length > 0 ? (
              <div className="space-y-4">
                {stats.recent_messages.map((message, index) => (
                  <div 
                    key={message.id || index}
                    data-testid={`recent-message-${index}`}
                    className={`p-4 rounded-lg border ${
                      !message.is_read ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-800 truncate">
                            {message.name}
                          </p>
                          {!message.is_read && (
                            <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                              Yeni
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 truncate">
                          {message.subject}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {message.email}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        {formatDate(message.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Henüz mesaj bulunmuyor</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="border-slate-200 hover:border-[#005CB2]/30 transition-colors cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#005CB2]/10 rounded-lg flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-[#005CB2]" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Kategorileri Yönet</p>
                <p className="text-sm text-slate-500">Hizmet kategorilerini düzenleyin</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:border-[#005CB2]/30 transition-colors cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#005CB2]/10 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-6 h-6 text-[#005CB2]" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">Mesajları Görüntüle</p>
                <p className="text-sm text-slate-500">İletişim formundan gelen mesajlar</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 hover:border-[#005CB2]/30 transition-colors cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-[#005CB2]/10 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#005CB2]" />
              </div>
              <div>
                <p className="font-semibold text-slate-800">İstatistikler</p>
                <p className="text-sm text-slate-500">Site performansını izleyin</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
